const axios = require("axios");
import { toastError } from "@/toastNotification.js";
import * as ENV from "../src/config.js";

function getPublicApiBaseUrl() {
  const base = String(ENV.VUE_APP_BACKEND_URL || "").trim();
  if (!base) return "";
  // Support both styles:
  // - http://localhost:8005
  // - http://localhost:8005/ba/api/public/
  if (base.includes("/ba/api/public/")) {
    return base.endsWith("/") ? base : base + "/";
  }
  return base.replace(/\/+$/, "") + "/ba/api/public/";
}

function publicApiUrl(path) {
  const base = getPublicApiBaseUrl();
  return base + String(path || "").replace(/^\/+/, "");
}

export const getConfig = async () => {
  const results = await axios.get(
    ENV.VUE_APP_CONFIG_API_URL + ENV.VUE_APP_FACILITY,
    {
      headers: {
        Authorization: "fakeauthkey",
      },
    }
  );
  return results.data;
};

export const getVenueOptionsByType = async (venueType) => {
  const results = await axios.get(
    ENV.VUE_APP_BACKEND_URL + "getvenue/" + encodeURIComponent(venueType),
    {
      headers: {
        Authorization: "fakeauthkey",
      },
    }
  );
  return results.data;
};

/**
 * Get calendar data for a venue with proper validation
 * Supports both regular venues and bundle venues
 * @param {string} facility - Facility name (e.g., "tennis")
 * @param {string} monthYear - Month and year in format "YYYY-MM" (e.g., "2025-10")
 * @param {string} uniqueVenueId - Unique venue ID from new API (e.g., "FCC-VEN-1")
 * @param {Array<number>} priavaIdList - List of venue IDs (e.g., [100493409] or [111, 222, 333])
 * @returns {Promise<Array>} Array of booked venue data or empty array if validation fails
 */
export const getCalendarData = async (facility, monthYear, uniqueVenueId, priavaIdList) => {
  try {
    // Validate inputs
    if (!facility || !monthYear || !uniqueVenueId || !priavaIdList) {
      return [];
    }

    // Validate monthYear format (YYYY-MM)
    if (!/^\d{4}-\d{2}$/.test(monthYear)) {
      return [];
    }

    // Validate priavaIdList is an array
    if (!Array.isArray(priavaIdList) || priavaIdList.length === 0) {
      return [];
    }

    // Construct the payload
    const payload = {
      Facility: facility,
      MonthYear: monthYear,
      UniqueVenueId: uniqueVenueId,
      PriavaId: priavaIdList
    };

    // Make the API call
    const response = await axios.post(
      ENV.VUE_APP_API_URL + "/getcalendar",
      payload,
      {
        headers: {
          Authorization: "fakeauthkey",
        }
      }
    );

    // Handle response
    if (response.data === null || response.data === undefined) {
      return [];
    }

    if (Array.isArray(response.data)) {
      return response.data;
    }

    return [];

  } catch (error) {
    return [];
  }
};

/**
 * Build calendar request for a venue
 * Handles both regular venues and bundle venues
 * @param {string} facility - Facility name
 * @param {string} monthYear - Month/Year in YYYY-MM format
 * @param {Object} venue - Venue object with id, venueid, and optionally bundlevenueids
 * @returns {Promise<Array>} Calendar data or empty array
 */
export const buildCalendarRequest = async (facility, monthYear, venue) => {
  try {
    // Validate venue object has required id
    if (!venue || !venue.id) {
      return [];
    }

    // Determine PriavaId list
    let priavaIdList = [];
    
    // Check if this is a bundle venue (has bundlevenueids array with items)
    if (venue.bundlevenueids && Array.isArray(venue.bundlevenueids) && venue.bundlevenueids.length > 0) {
      priavaIdList = venue.bundlevenueids;
    } else if (venue.venueid !== undefined) {
      // Regular venue - wrap single venueid in array
      priavaIdList = [venue.venueid];
    } else {
      return [];
    }

    // Use the getCalendarData function with venue data
    return await getCalendarData(
      facility,
      monthYear,
      venue.id,              // UniqueVenueId from new API
      priavaIdList           // PriavaId - list of numeric venue IDs
    );

  } catch (error) {
    return [];
  }
};

export const saveConfig = async (data) => {
  await axios.post(
    ENV.VUE_APP_CONFIG_API_URL + ENV.VUE_APP_FACILITY,
    {
      ...data,
    },
    {
      headers: {
        Authorization: "fakeauthkey",
      },
    }
  );
};

export const createBooking = async () => {
  return {
    data: {
      bookingId: "1234",
      status: "booking_initiated",
    },
    status: "success",
    statusCode: 200,
  };
};

export const setBookingDetail = async (bookingDetail) => {
  const results = await axios.post(
    ENV.VUE_APP_BACKEND_URL + "bookings/details/" + ENV.VUE_APP_FACILITY,
    JSON.stringify(bookingDetail),
    {
      headers: {
        Authorization: "fakeauthkey",
      },
    }
  );
  return results.data;
};

export const setBookingSearchSelect = async (vanueDetails) => {
  const results = await axios.post(
    ENV.VUE_APP_BACKEND_URL + "bookings/searchselect/" + ENV.VUE_APP_FACILITY,
    JSON.stringify(vanueDetails),
    {
      headers: {
        Authorization: "fakeauthkey",
      },
    }
  );
  return results.data;
};

// Payment gateway APIs transaction
// ToDo : Needs to be move on backend in future
export const generateAuthKeys = async () => {
  const results = await axios.get(ENV.VUE_APP_BACKEND_URL + "authkey", {});
  return results;
};

export const attachTransactionDetail = async (
  authkey,
  rate,
  crn2,
  venueId,
  firstname,
  lastName,
  email,
  venueBlockId
) => {
  const venueIdForCrn = Array.isArray(venueId)
    ? venueId.join("-")
    : venueId.toString();
  const vb = Array.isArray(venueBlockId) ? venueBlockId[0] : venueBlockId;
  const venueBlockIdForCrn =
    vb != null && String(vb).trim() !== "" ? String(vb).trim() : "";
  const crn3Reference = venueBlockIdForCrn
    ? `${venueIdForCrn}-${venueBlockIdForCrn}`
    : venueIdForCrn;
  let crn3 = `${crn3Reference}${firstname}${lastName}`;

  if (crn3.length > 50) {
    crn3 = crn3.substring(0, 49);
  }

  const emailParam =
    email != null && String(email).trim() !== ""
      ? `&email=${encodeURIComponent(String(email).trim())}`
      : "";

  let venueBlockParam = "";
  if (venueBlockIdForCrn !== "") {
    venueBlockParam = `&venueblockid=${encodeURIComponent(venueBlockIdForCrn)}`;
  }

  let query = `?rate=${rate}&crn2=${crn2}&crn3=${crn3}${emailParam}${venueBlockParam}`;
  const results = await axios.post(
    ENV.VUE_APP_BACKEND_URL +
      `attatchtxndetails/` +
      ENV.VUE_APP_FACILITY +
      `${query}`,
    {},
    {
      headers: {
        AuthKey: authkey,
      },
    }
  );
  return results;
};

export const hppConfiguration = async (authkey) => {
  // let newVenueType = venuetype.replaceAll(" ", "_");
  let facitlity = ENV.VUE_APP_FACILITY;
  let host = window.location.host;
  let protocol = window.location.protocol;

  let builtURL =
    protocol +
    "//" +
    host +
    "/" +
    "bookingapp/" +
    facitlity +
    "/" +
    "summarypage";
  // let previousURL =
  //   protocol +
  //   "//" +
  //   host +
  //   "/" +
  //   "bookingapp/" +
  //   facitlity +
  //   "/" +
  //   "details/" +
  //   "?venueType=" +
  //   newVenueType;
  const results = await axios.post(
    ENV.VUE_APP_BACKEND_URL + `hppconfiguration`,
    {
      redirectionUrl: builtURL,
      tokeniseTxnCheckBoxDefaultValue: true,
      hideCRN1: true,
      hideCRN2: true,
      hideCRN3: true,
      hideBillerCode: true,
      //returnBarLabel: "Go Back",
      //returnBarUrl: previousURL,
    },
    {
      headers: {
        AuthKey: authkey,
      },
    }
  );
  return results;
};

export const processTransaction = async (authkey) => {
  const results = await axios.post(
    ENV.VUE_APP_BACKEND_URL + `processtransactionrequest`,
    {
      webhook: {
        url: "http://localhost:9000",
        version: "5",
      },
      surcharge: {
        calculate: false,
        amount: 0,
      },
      updateToken: true,
    },
    {
      headers: {
        AuthKey: authkey,
      },
    }
  );
  return results;
};

export const getPricingDetail = async (venueBlockID) => {
  const results = await axios.post(
    ENV.VUE_APP_BACKEND_URL + "bookings/pricing/" + ENV.VUE_APP_FACILITY,
    JSON.stringify({
      VenueBlockId: venueBlockID,
      Status: "",
    }),
    {
      headers: {
        Authorization: "fakeauthkey",
      },
    }
  );
  return results.data;
};

export const confirmBooking = async (venueBlockID, txnDetail, bookingType) => {
  // const getBookingType = bookingType ? 'multibooking' : 'singlebooking'
  const results = await axios.post(
    ENV.VUE_APP_BACKEND_URL +
      "bookings/confirmed/" +
      ENV.VUE_APP_FACILITY +
      "/" +
      bookingType,
    JSON.stringify({
      venueBlockId: {
        venueblockid: venueBlockID,
      },
      PaymentDetails: {
        txn: txnDetail,
      },
    }),
    {
      headers: {
        Authorization: "fakeauthkey",
      },
    }
  );
  return results.data;
};

// Get Result key after success of payment
export const getResult = async (resultKey) => {
  const results = await axios.get(ENV.VUE_APP_BACKEND_URL + "resultkey", {
    headers: {
      resultKey: resultKey,
    },
  });
  return results.data;
};

// NEW APIS

// VENUE SETTINGS

// addVenueType
export const addVenueTypeSettings = async (data) => {
  const result = await axios.post(
    ENV.VUE_APP_ADMIN_API_URL + "addvenuetype/" + ENV.VUE_APP_FACILITY,
    [
      {
        venuetypesetting: data,
      },
    ],
    {
      headers: {
        Authorization: "fakeauthkey",
      },
    }
  );
  return result.data;
};
// delete venueType
export const deleteVenueTypeSettings = async (data, details) => {
  const result = await axios.post(
    ENV.VUE_APP_ADMIN_API_URL + "deletevenuetype/" + ENV.VUE_APP_FACILITY,
    [
      {
        venuetypesetting: data,
        detailSettings: [
          {
            detailSettingSection: [...details],
          },
        ],
      },
    ],
    {
      headers: {
        Authorization: "fakeauthkey",
      },
    }
  );
  return result.data;
};

// Add Venues
export const addVenueSettings = async (data) => {
  const result = await axios.post(
    ENV.VUE_APP_ADMIN_API_URL + "addvenue/" + ENV.VUE_APP_FACILITY,
    [data],
    {
      headers: {
        Authorization: "fakeauthkey",
      },
    }
  );
  return result.data;
};

// GET Venues Settings
export const GetVenueSettings = async () => {
  const result = await axios.get(
    ENV.VUE_APP_ADMIN_API_URL + "getvenuedetails/" + ENV.VUE_APP_FACILITY,
    {
      headers: {
        Authorization: "fakeauthkey",
      },
    }
  );
  return result.data;
};

// DETAILS SECTIONS
//Get Details
export const GetDetailsSettings = async () => {
  const result = await axios.get(
    ENV.VUE_APP_ADMIN_API_URL + "getdetailssettings/" + ENV.VUE_APP_FACILITY,
    {
      headers: {
        Authorization: "fakeauthkey",
      },
    }
  );
  return result.data;
};

//UPDATE ADMIN DETAILS
export const UpdateDetailsSettings = async (data) => {
  const result = await axios.post(
    ENV.VUE_APP_ADMIN_API_URL + "updatedetails/" + ENV.VUE_APP_FACILITY,
    data,
    {
      headers: {
        Authorization: "fakeauthkey",
      },
    }
  );
  return result.data;
};

// Get SEARCH SETTINGS
export const GetSearchSettings = async () => {
  const result = await axios.get(
    ENV.VUE_APP_ADMIN_API_URL + "getsearchdetails/" + ENV.VUE_APP_FACILITY,
    {
      headers: {
        Authorization: "fakeauthkey",
      },
    }
  );
  return result.data;
};

// UIPDATE ADMIN SEARCHING
export const UpdateSearchSettings = async (data) => {
  const result = await axios.post(
    ENV.VUE_APP_ADMIN_API_URL + "updatesearchoption/" + ENV.VUE_APP_FACILITY,
    // {
    data.bookingOptions,
    // }
    {
      headers: {
        Authorization: "fakeauthkey",
      },
    }
  );
  return result.data;
};

// Update Admin Configuration
export const UpdateConfigurationSettings = async (data) => {
  const result = await axios.post(
    ENV.VUE_APP_ADMIN_API_URL + "general/config/" + ENV.VUE_APP_FACILITY,
    {
      color: data.color,
      fontColor: data.fontColor,
      title: data.header.title,
      rightLogo: data.header.rightLogo,
      leftLogo: data.header.leftLogo,
    },
    {
      headers: {
        Authorization: "fakeauthkey",
      },
    }
  );
  return result.data;
};

// Update terms and conditions
export const UpdatetermsAndConditionsSettings = async (data) => {
  const result = await axios.post(
    ENV.VUE_APP_ADMIN_API_URL + "updateterms/" + ENV.VUE_APP_FACILITY,
    [...data],
    {
      headers: {
        Authorization: "fakeauthkey",
      },
    }
  );
  return result.data;
};

// Update notifications
export const UpdateNotificationSettings = async (data) => {
  const result = await axios.post(
    ENV.VUE_APP_ADMIN_API_URL + "general/notification/" + ENV.VUE_APP_FACILITY,
    [...data],
    {
      headers: {
        Authorization: "fakeauthkey",
      },
    }
  );
  return result.data;
};

// Update working hours
export const UpdateWorkingHoursSettings = async (data) => {
  const result = await axios.post(
    ENV.VUE_APP_ADMIN_API_URL + "updateworkinghours/" + ENV.VUE_APP_FACILITY,
    data.workingHoursConfig,
    {
      headers: {
        Authorization: "fakeauthkey",
      },
    }
  );
  return result.data;
};

// Get General Settings
export const GetgeneralSettings = async () => {
  const result = await axios.get(
    ENV.VUE_APP_ADMIN_API_URL + "getgeneralsettings/" + ENV.VUE_APP_FACILITY,
    {
      headers: {
        Authorization: "fakeauthkey",
      },
    }
  );
  return result.data;
};

export const UpdateAdminGeneralSettingText = async (data) => {
  const result = await axios.post(
    ENV.VUE_APP_ADMIN_API_URL + "updatetext/" + ENV.VUE_APP_FACILITY,
    JSON.stringify({
      text: data,
    }),
    {
      headers: {
        Authorization: "fakeauthkey",
      },
    }
  );
  return result.data;
};

// Acticve inactive second details page
export const ActoiveSecondDetailsPage = async (data) => {
  const result = await axios.post(
    ENV.VUE_APP_ADMIN_API_URL + "activedetails/" + ENV.VUE_APP_FACILITY,
    [
      {
        isactive: data,
      },
    ],
    {
      headers: {
        Authorization: "fakeauthkey",
      },
    }
  );
  return result.data;
};

// Get VenueType
export const GetVenueTypeSettings = async () => {
  const result = await axios.get(
    ENV.VUE_APP_ADMIN_API_URL + "getvenuetype/" + ENV.VUE_APP_FACILITY,
    {
      headers: {
        Authorization: "fakeauthkey",
      },
    }
  );
  return result.data;
};

// Create Facility
export const CreateFacility = async (data) => {
  const result = await axios.post(
    ENV.VUE_APP_ADMIN_API_URL + "createfacility/" + ENV.VUE_APP_FACILITY,
    [data],
    {
      headers: {
        Authorization: "fakeauthkey",
      },
    }
  );
  return result.data;
};

// Get facility
export const GetPaymentSettings = async () => {
  const result = await axios.get(
    ENV.VUE_APP_ADMIN_API_URL + "getfacility/" + ENV.VUE_APP_FACILITY,
    {
      headers: {
        Authorization: "fakeauthkey",
      },
    }
  );
  return result.data;
};

// Delete Facility
export const DeleteFacility = async (data) => {
  const result = await axios.post(
    ENV.VUE_APP_ADMIN_API_URL + "deletefacility/" + ENV.VUE_APP_FACILITY,
    [data],
    {
      headers: {
        Authorization: "fakeauthkey",
      },
    }
  );
  return result.data;
};

// Save image
export const SaveImage = async (data, venueBlockid) => {
  const result = await axios.post(
    ENV.VUE_APP_BACKEND_URL + "saveimage/" + venueBlockid,
    data,
    {
      headers: {
        Authorization: "fakeauthkey",
      },
    }
  );
  return result.data;
};

// Block Venues
export const AddBlockVenueTime = async (data) => {
  const result = await axios.post(
    ENV.VUE_APP_ADMIN_API_URL + "blockvenues/" + ENV.VUE_APP_FACILITY + "/ADD",
    [data],
    {
      headers: {
        Authorization: "fakeauthkey",
      },
    }
  );
  return result.data;
};

export const DeleteBlockVenueTime = async (data) => {
  const result = await axios.post(
    ENV.VUE_APP_ADMIN_API_URL +
      "blockvenues/" +
      ENV.VUE_APP_FACILITY +
      "/DELETE",
    JSON.stringify([data]),
    {
      headers: {
        Authorization: "fakeauthkey",
      },
    }
  );
  return result.data;
};

// Block Venues
export const getBlockVenueTime = async (monthYear) => {
  const result = await axios.get(
    ENV.VUE_APP_ADMIN_API_URL +
      "blockvenues/" +
      ENV.VUE_APP_FACILITY +
      "/get" +
      `?monthyear=${monthYear}`,
    {
      headers: {
        Authorization: "fakeauthkey",
      },
    }
  );
  return result.data;
};

// MultiBooking apis

export const multiBookingsearch = async (data) => {
  const res = await axios.post(
    `${ENV.VUE_APP_API_URL}/multisearchbookingsdb/${ENV.VUE_APP_FACILITY}`,
    {
      ...data,
      CreatedDate: "",
      LastUpdatedDate: "",
    }
  );
  return res.data;
};

export const setMultiBookingSearchSelect = async (vanueDetails) => {
  const results = await axios.post(
    ENV.VUE_APP_BACKEND_URL +
      "bookings/multisearchselect/" +
      ENV.VUE_APP_FACILITY,
    JSON.stringify(vanueDetails),
    {
      headers: {
        Authorization: "fakeauthkey",
      },
    }
  );
  return results.data;
};

export const setBundleSearchSelect = async (vanueDetails) => {
  const results = await axios.post(
    ENV.VUE_APP_BACKEND_URL +
      "bookings/bundlesearchselect/" +
      ENV.VUE_APP_FACILITY,
    JSON.stringify(vanueDetails),
    {
      headers: {
        Authorization: "fakeauthkey",
      },
    }
  );
  return results.data;
};

export const setmultiBookingDetail = async (bookingDetail, uniqueVenueId) => {
  const url = ENV.VUE_APP_BACKEND_URL +
    "bookings/multibookingdetails/" +
    ENV.VUE_APP_FACILITY;
  
  const urlWithId = uniqueVenueId ? `${url}?id=${uniqueVenueId}` : url;
  
  const results = await axios.post(
    urlWithId,
    JSON.stringify(bookingDetail),
    {
      headers: {
        Authorization: "fakeauthkey",
      },
    }
  );
  return results.data;
};

export const setBundleBookingDetail = async (bookingDetail, bundleId) => {
  const results = await axios.post(
    ENV.VUE_APP_BACKEND_URL +
      "bookings/bundlebookingdetails/" +
      ENV.VUE_APP_FACILITY + 
      `?bundleId=${bundleId}`,
    JSON.stringify(bookingDetail),
    {
      headers: {
        Authorization: "fakeauthkey",
      },
    }
  );
  return results.data;
};

export const getmultiPricingDetail = async (venueBlockID) => {
  const results = await axios.post(
    ENV.VUE_APP_BACKEND_URL +
      "bookings/multibookingpricing/" +
      ENV.VUE_APP_FACILITY,
    JSON.stringify({
      VenueBlockId: [venueBlockID],
      Status: "",
    }),
    {
      headers: {
        Authorization: "fakeauthkey",
      },
    }
  );
  return results.data;
};

export const getPriceBookingPage = async (vanueDetails) => {
  const results = await axios.post(
    ENV.VUE_APP_BACKEND_URL + "bookings/getpricing/" + ENV.VUE_APP_FACILITY,
    JSON.stringify(vanueDetails)
    // {
    //     headers: {
    //         Authorization: "fakeauthkey",
    //     }
    // }
  );
  return results.data;
};

// Get Venues Price From DB
export const getVenuesPrice = async () => {
  const result = await axios.get(
    ENV.VUE_APP_ADMIN_API_URL + "getadminvenuesprice/" + ENV.VUE_APP_FACILITY,
    {
      headers: {
        Authorization: "fakeauthkey",
      },
    }
  );
  return result.data;
};

export const addAddonFacility = async (overlap, nonoverlap) => {
  const results = await axios.post(
    ENV.VUE_APP_BACKEND_URL + "addaddon/" + ENV.VUE_APP_FACILITY,
    [overlap, nonoverlap],
    {
      headers: {
        Authorization: "fakeauthkey",
      },
    }
  );
  return results.data;
};

export const checkOverlapping = async (nonoverlap) => {
  
  try {
    const results = await axios.post(
      ENV.VUE_APP_BACKEND_URL + "checkconflictaddon/" + ENV.VUE_APP_FACILITY,
      nonoverlap,
      {
        headers: {
          Authorization: "fakeauthkey",
        },
      }
    );
    return results.data;
  } catch (error) {
    console.error("Error checking for overlapping bookings:", error);
    throw error;
  }
};

// Create singlebooking with direct booking forms
export const createDirectSingleBookingWithForm = async (data) => {
  try {
    const result = await axios.post(
      ENV.VUE_APP_BACKEND_URL + "directbooking/" + ENV.VUE_APP_FACILITY,
      data
    );
    return result.data;
  } catch (error) {
    console.log("ERROR from createDirectSingleBookingWithForm:: ", error);
    return error.response;
  }
};

// Onetime Login
export const onetimeLogin = async (email, otpCode, venueType) => {
  try {
    const result = await axios.post(
      ENV.VUE_APP_BACKEND_URL +
        "bookings/" +
        ENV.VUE_APP_FACILITY +
        "/onetimelogin",
      JSON.stringify({
        email,
        venuetype: venueType,
        venueType,
        opt_code: otpCode,
        otp_code: otpCode,
      }),
      {
        headers: {
          "Content-Type": "application/json",
        },
      }
    );
    return result.data;
  } catch (error) {
    console.log("ERROR from onetimeLogin:: ", error);
    return error.response?.data || { status: 0, message: "Login failed" };
  }
};

// check Onetime Token
export const verifyOnetimeToken = async (token) => {
  try {
    const result = await axios.post(
      ENV.VUE_APP_BACKEND_URL +
        "bookings/" +
        ENV.VUE_APP_FACILITY +
        "/verifytoken",
      JSON.stringify({
        accessToken: token,
      }),
      {
        headers: {
          "Content-Type": "application/json",
        },
      }
    );
    return result.data;
  } catch (error) {
    console.log("ERROR from verifyOnetimeToken:: ", error);
    return error.response?.data || { status: 0, message: "Token is not valid" };
  }
};

// check existing OTP email
export const checkExistingOnetimeUser = async (email, venueType) => {
  try {
    const result = await axios.post(
      ENV.VUE_APP_BACKEND_URL +
        "bookings/" +
        ENV.VUE_APP_FACILITY +
        "/getotpuser",
      JSON.stringify({
        email,
        venuetype: venueType,
        venueType,
      }),
      {
        headers: {
          "Content-Type": "application/json",
        },
      }
    );
    return result.data;
  } catch (error) {
    // Handle different error scenarios
    if (error.response && error.response.data) {
      const errorData = error.response.data;
      
      // Check for specific error status
      if (errorData.status === 2) {
        toastError(errorData.message || "Your account is not active yet");
        return { status: 2, message: errorData.message, response: {} };
      }
      
      // Return error response if it exists
      if (errorData.status !== undefined) {
        toastError(errorData.message || "Unable to verify your email. Please try again.");
        return { status: errorData.status, message: errorData.message, response: {} };
      }
    }
    
    // Handle network errors or unauthorized access
    if (error.response && error.response.status === 401) {
      toastError("You are not authorized to access this system.");
      return { status: 401, message: "Unauthorized", response: {} };
    }
    
    if (error.response && error.response.status === 404) {
      toastError("User not found. Please register first.");
      return { status: 404, message: "User not found", response: {} };
    }
    
    // Generic error handling
    console.log("ERROR from check existing onetime user:: ", error);
    toastError("Unable to verify your email. Please try again.");
    return { status: 0, message: "Request failed", response: {} };
  }
};

// send OTP to email / phone
export const sendOTPToEmail = async (email, venueType, phone) => {
  try {
    const result = await axios.post(
      ENV.VUE_APP_BACKEND_URL +
        "bookings/" +
        ENV.VUE_APP_FACILITY +
        "/generateotp/login",
      JSON.stringify({
        email,
        venuetype: venueType,
        venueType,
        phone,
      }),
      {
        headers: {
          "Content-Type": "application/json",
        },
      }
    );
    return result.data;
  } catch (error) {
    console.log("ERROR from send OTP to email:: ", error);
    if (error.response?.data) {
      return error.response.data;
    }
    return { status: 0, message: "Unable to send OTP. Please try again." };
  }
};

// Get Venue Terms and Conditions with Summary
export const getVenueTermsAndConditionsWithSummary = async (venueID) => {
  try {
    const result = await axios.get(
      ENV.VUE_APP_ADMIN_API_URL +
        "venuetc/" +
        ENV.VUE_APP_FACILITY +
        "/" +
        venueID,
      {
        headers: {
          Authorization: "Bearer " + "fakeauthkey",
          username: sessionStorage.getItem("username"),
        },
      }
    );
    return result.data;
  } catch (error) {
    toastError("Fail to update venue summary.");
    console.log("ERROR::updateVenueSummary:: ", error);
  }
};


export const getVenueWorkingHours = async (venueID) => {
  try {
    const result = await axios.get(
      ENV.VUE_APP_ADMIN_API_URL +
        "venuehours/" +
        ENV.VUE_APP_FACILITY +
        "/" +
        venueID,
      {
        headers: {
          Authorization: "Bearer " + "fakeauthkey",
          username: sessionStorage.getItem("username"),
        }
      }
    );
    return result.data;
  } catch (error) {
    console.error("ERROR::getVenueWorkingHours::", error);
    return { workinghours: [] };
  }
};

// GET Venues Settings
export const GetFreeSlots = async () => {
  const result = await axios.get(
    ENV.VUE_APP_BACKEND_URL + "freebooking/availability?isBooked=false"
  );
  let slots = [];
  for (let i = 0; i < result.data.freebooking.length; i++) {
    slots.push({
      label: result.data.freebooking[i].name,
      value: result.data.freebooking[i].id, 
    });
  }
  return slots;
};

/**
 * Child seat slot list: GET …/childseat/availability?isBooked=false
 */
export const GetEventsBookingSlots = async () => {
  try {
    const result = await axios.get(
      publicApiUrl("/childseat/availability?isBooked=false")
    );
    const raw =
      result?.data?.data ??
      result?.data?.childseat ??
      result?.data?.freebooking ??
      result?.data?.slots;
    if (!Array.isArray(raw)) return [];
    return raw.map((s) => ({
      label: s.name ?? s.label ?? String(s._id ?? s.id ?? ""),
      value: s._id ?? s.id ?? s.value,
    }));
  } catch {
    return [];
  }
};

export const submitEventsBooking = async (payload) => {
  try {
    const result = await axios.post(
      publicApiUrl("/childseat/freebooking"),
      payload,
      {
        headers: {
          Authorization: "fakeauthkey",
        },
      }
    );
    return { status: result.status, data: result.data };
  } catch (error) {
    return {
      status: error?.response?.status || 0,
      data:
        error?.response?.data || {
          message: error?.message || "Network Error",
        },
    };
  }
};

// Create singlebooking with free booking forms
export const createFreeSingleBookingWithForm = async (data) => {
  try {
    const result = await axios.post(
      ENV.VUE_APP_BACKEND_URL + "freebooking",
      data
    );
    return { status: result.status, data: result.data };
  } catch (error) {
    console.log("ERROR from createFreeSingleBookingWithForm:: ", error);
    // When CORS blocks the request, the browser surfaces it as a network error and
    // `error.response` is undefined. Return a safe shape so the UI can react.
    return {
      status: error?.response?.status || 0,
      data: error?.response?.data || { message: error?.message || "Network Error" },
    };
  }
};